import { Injectable } from '@angular/core';
import { Observable, } from 'rxjs/Observable';
import { CookieHelperService } from './cookie-helper.service';
import { ApiService } from './auto-loans/api.service';
import { HttpClient } from '@angular/common/http';
import { Subject, } from 'rxjs/Subject';
import { SettingsService } from './auto-loans/settings.service';
import { ICarzingTokenConfig, IEndpointConfig } from './auto-loans/interfaces/auto-loans.interface';

@Injectable()
export class SessionHelperService extends ApiService {

    private currentSession: any = null;
    private sessionToken: string = null;

    private carzingSession: Observable<any> = null;
    private carzingSessionSub: Subject<any> = new Subject<any>();

    get CzAuthTokenKey (): string {
        return `${this.CzConfig.prefix}.${this.CzConfig.version}`;
    }

    get CzAuthToken (): string {
        return this.cookieHelper._getCookie(this.CzAuthTokenKey);
    }

    get CzConfig (): ICarzingTokenConfig {
        return this._settingService.CzTokenConfig;
    }

    get endpointConfig (): IEndpointConfig {
        return this._settingService.endpointConfig;
    }
    
    constructor(
        protected _http: HttpClient,
        protected _settingService: SettingsService,
        protected cookieHelper: CookieHelperService
    ) {
        super(_http);
        this.initSession();
    }

    endpointGenerator (endpoint: string): string {
        let url: string = `/nowcom-wfs-direct/service/${this.endpointConfig.protocol}/${this.endpointConfig.domain}`;
        return `${url}/${endpoint}`;
    }
  
    private initSession () {
        // Generate Carzing Token //
        this.carzingSession = new Observable(observer => {
            if (this.CzAuthToken) {
                this.carzingSession = null;
                this.carzingSessionSub.next(this.CzAuthToken);
                observer.complete();
                return;
            }
            this.postRaw(
                this.endpointGenerator(this.CzConfig.endpoint)
            ).subscribe(
                response => {
                    this.carzingSession = null;
                    this.carzingSessionSub.next(response);
                    if (response) {
                        this.cookieHelper._setCookie(this.CzAuthTokenKey, response.Token, response.Expires);
                    }
                    observer.next(response);
                    observer.complete();
                },
                error => {
                    this.carzingSession = null;
                    this.carzingSessionSub.next(error);
                    observer.error(error);
                    observer.complete();
                }
            );
        });
        this.carzingSession.subscribe(
            response => {},
            error => {}
        );
    }

    private onGetSessionToken (sessionGUID: string, observer) {
        if (sessionGUID) {
            return observer.next(sessionGUID);
        }
        let currentSession = this.cookieHelper._getCookie('WDSession');
        if (currentSession !== null && currentSession !== undefined) {
            return observer.next(currentSession);
        }
        currentSession = this.sessionToken;
        if (currentSession !== null && currentSession !== undefined) {
            return observer.next(currentSession);
        }
        observer.next(null);
    }

    private getSessionToken (sessionGUID: string = null): Observable <string> {
        return new Observable((observer) => {
            if (this.carzingSession !== null) {
                this.carzingSessionSub.subscribe(response => {
                    this.onGetSessionToken(sessionGUID, observer);
                    observer.complete();
                }, error => {
                    this.onGetSessionToken(sessionGUID, observer);
                    observer.complete();
                });
                return;
            }
            this.onGetSessionToken(sessionGUID, observer);
            observer.complete();
        });
    }

    private onStartSession (observer) {
        this.postRaw('/wfs-direct/api/session/', {
            request_type: 'GENERATE_SESSION'
        }).subscribe(
            res => {
                this.sessionToken = res.data;
                observer.next(this.sessionToken);
                observer.complete();
            },
            err => {
                observer.error(err);
                observer.complete();
            }
        );
    }

    startSession (): Observable<any> {
        return new Observable(observer => {
            if (this.carzingSession !== null) {
                this.carzingSessionSub.subscribe(response => {
                    this.onStartSession(observer);
                }, error => {
                    this.onStartSession(observer);
                });
                return;
            }
            this.onStartSession(observer);
        });
    }

    onGetIpInfo (observer) {
        this.postRaw(
            '/wfs-direct/api/session/', {
                request_type: 'IP_INFO',
            }
        ).subscribe(
            response => {
                observer.next(response);
                observer.complete();
            },
            error => {
                observer.error(error);
                observer.complete();
            }
        );
    }

    getIpInfo (): Observable<any> {
        return new Observable<any>(observer => {
            if (this.carzingSession !== null) {
                this.carzingSessionSub.subscribe(
                    response => this.onGetIpInfo(observer),
                    error => this.onGetIpInfo(observer)
                );
                return;
            }
            this.onGetIpInfo(observer);
        });
    }

    setSession (sessionKey: string, value: any, sessionGUID: string = null): Observable<boolean> {
        return new Observable(observer => {
            this.getSessionToken(sessionGUID)
                .subscribe(sessionToken => {
                    this.postRaw('/wfs-direct/api/session/', {
                        request_type: 'SET_SESSION',
                        session_token: sessionToken,
                        data: [{
                            value,
                            key: sessionKey
                        }]
                    }).subscribe(
                        res => { this.currentSession = res.data; observer.next(true); },
                        err => observer.error(false),
                        () => observer.complete()
                    );
                });
        });
    }

    setSessions (sessionData: {
        key?: string;
        value?: any
    }[], sessionGUID: string = null): Observable<boolean> {
        return new Observable(observer => {
            this.getSessionToken(sessionGUID)
                .subscribe(sessionToken => {
                    this.postRaw('/wfs-direct/api/session/', {
                        request_type: 'SET_SESSION',
                        session_token: sessionToken,
                        data: sessionData
                    }).subscribe(
                        res => { this.currentSession = res.data; observer.next(true); },
                        err => observer.next(false),
                        () => observer.complete()
                    );
                });
        });
    }

    getSession (sessionKey: string | string[] = null, sessionGUID: string = null): Observable<any> {
        return new Observable(observer => {
            this.getSessionToken(sessionGUID)
                .subscribe(sessionToken => {

                    if (this.currentSession !== null) {
                        if (sessionKey === null) {
                            observer.next(this.currentSession);
                            observer.complete();
                            return;
                        }

                        if (typeof sessionKey === 'string') {
                            if (this.currentSession[sessionKey] !== undefined && this.currentSession[sessionKey] !== null) {
                                observer.next(this.currentSession[sessionKey]);
                                observer.complete();
                                return;
                            }
                        } else {
                            const sessionKeys = Object.keys(this.currentSession);
                            const results = sessionKeys.map(key =>
                                this.currentSession[key] !== undefined ? this.currentSession[key] : null
                            );
                        }
                    }

                    this.postRaw('/wfs-direct/api/session/', {
                        request_type: 'GET_SESSION',
                        session_token: sessionToken,
                        data: {
                            session_key: sessionKey ? sessionKey : null
                        }
                    }).subscribe(
                        res => {
                            let data = res.data;
                            try {
                                data = atob(res.data);
                            } catch (e) {
                            }

                            if (typeof data === 'string') {
                                try {
                                    data = JSON.parse(data);
                                } catch (e) {
                                }
                            }

                            if (sessionKey === null) {
                                this.currentSession = data;
                            } else {
                                if (this.currentSession === null) {
                                    this.currentSession = {};
                                }
                                if (typeof sessionKey === 'string') {
                                    this.currentSession[sessionKey] = data;
                                } else {
                                    const sessionKeys = Object.keys(this.currentSession);
                                    sessionKeys.map(key =>
                                        this.currentSession[key] !== undefined ? this.currentSession[key] : null
                                    );
                                }
                            }
                            observer.next(res.data);
                        },
                        err => observer.error(err),
                        () => observer.complete()
                    );
                });
        });
    }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/session-helper.service.ts